import React, { useEffect, useRef, useState } from "react";
import { Box, Container, Typography } from "@mui/material";
import { motion, useMotionValue, useSpring } from "framer-motion";

/* ══════════════════════════════════════════════════════════════════
   GLOBAL STYLES
══════════════════════════════════════════════════════════════════ */
const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap');

    @keyframes ts-breathe {
      0%,100% { opacity:.4; transform:scale(1); }
      50%      { opacity:.8; transform:scale(1.07); }
    }
    @keyframes ts-orb {
      0%,100% { transform:translate(0,0) scale(1); }
      30%     { transform:translate(16px,-12px) scale(1.05); }
      70%     { transform:translate(-10px,16px) scale(.97); }
    }
    @keyframes ts-scanX {
      0%   { transform:translateX(-110%); opacity:0; }
      10%  { opacity:.5; }
      90%  { opacity:.3; }
      100% { transform:translateX(110%); opacity:0; }
    }
    @keyframes ts-blink {
      0%,100% { opacity:1; }
      48%,52% { opacity:.08; }
    }
    @keyframes ts-shimmer {
      0%   { background-position:0% center; }
      100% { background-position:220% center; }
    }
    @keyframes ts-countUp {
      0%   { opacity:0; transform:translateY(8px); }
      100% { opacity:1; transform:translateY(0); }
    }
    @keyframes ts-float {
      0%,100% { transform:translateY(0px); }
      50%     { transform:translateY(-8px); }
    }
    @keyframes ts-ring {
      0%   { transform:scale(.85); opacity:.7; }
      100% { transform:scale(2.4); opacity:0; }
    }
    @keyframes ts-borderGlow {
      0%,100% { box-shadow:0 0 0 1px rgba(30,144,255,.14), 0 28px 72px rgba(0,0,0,.65); }
      50%     { box-shadow:0 0 0 1px rgba(30,144,255,.32), 0 36px 90px rgba(30,144,255,.09); }
    }
    @keyframes ts-lineGrow {
      0%   { width:0; opacity:0; }
      100% { width:100%; opacity:1; }
    }
    @keyframes ts-dotPulse {
      0%,100% { box-shadow:0 0 0 0 rgba(30,144,255,.55), 0 0 6px rgba(30,144,255,.7); }
      50%     { box-shadow:0 0 0 5px rgba(30,144,255,0), 0 0 14px rgba(30,144,255,.9); }
    }
    @keyframes ts-tagIn {
      0%   { opacity:0; transform:translateY(6px) scale(.96); }
      100% { opacity:1; transform:translateY(0) scale(1); }
    }
  `}</style>
);

/* ══════════════════════════════════════════════════════════════════
   CONSTANTS
══════════════════════════════════════════════════════════════════ */
const rng = (s) => { const x = Math.sin(s+1)*10000; return x-Math.floor(x); };

const STATS = [
  { value:"40+",  label:"Team Members"      },
  { value:"12+",  label:"Domains Covered"   },
  { value:"98%",  label:"Retention Rate"    },
  { value:"3×",   label:"YoY Growth"        },
];

const TRAITS = [
  "Risk-takers",
  "Continuous Learners",
  "Challenge Chasers",
  "Mission-Driven",
  "Collaborators",
  "Innovators",
];

const DOTS = Array.from({ length: 22 }, (_, i) => ({
  id: i,
  x:    rng(i*3.14)*100,
  y:    rng(i*2.71)*100,
  size: rng(i*1.41)*2.4 + 0.5,
  op:   rng(i*1.61)*0.14 + 0.025,
  blue: rng(i*9.99) > 0.45,
  durY: rng(i*5.55)*20+12,
  durX: rng(i*7.77)*15+9,
  dy:   (rng(i*4.44)-.5)*44,
  dx:   (rng(i*6.66)-.5)*28,
  dlyY: rng(i*2.22)*-20,
  dlyX: rng(i*3.33)*-14,
}));

const easeOut = [0.16,1,0.3,1];

const inView = (delay=0, y=24) => ({
  initial:     { opacity:0, y },
  whileInView: { opacity:1, y:0 },
  viewport:    { once:true, margin:"-60px" },
  transition:  { duration:0.82, delay, ease:easeOut },
});

/* ══════════════════════════════════════════════════════════════════
   MINI PARTICLE FIELD
══════════════════════════════════════════════════════════════════ */
const Particles = () => (
  <Box aria-hidden sx={{ position:"absolute", inset:0, overflow:"hidden", pointerEvents:"none", zIndex:0 }}>
    {DOTS.map(p => (
      <Box
        key={p.id}
        component={motion.div}
        animate={{ y:[0,p.dy,0], x:[0,p.dx,0] }}
        transition={{
          y:{ duration:p.durY, repeat:Infinity, ease:"easeInOut", delay:p.dlyY },
          x:{ duration:p.durX, repeat:Infinity, ease:"easeInOut", delay:p.dlyX },
        }}
        sx={{
          position:"absolute",
          left:`${p.x}%`, top:`${p.y}%`,
          width:`${p.size}px`, height:`${p.size}px`,
          borderRadius:"50%",
          background: p.blue
            ? `rgba(30,144,255,${(p.op*3.5).toFixed(3)})`
            : `rgba(255,255,255,${p.op.toFixed(3)})`,
          boxShadow: p.blue && p.size>1.8 ? `0 0 ${p.size*4}px rgba(30,144,255,.45)` : "none",
          willChange:"transform",
        }}
      />
    ))}
  </Box>
);

/* ══════════════════════════════════════════════════════════════════
   STAT CARD
══════════════════════════════════════════════════════════════════ */
const StatCard = ({ value, label, delay }) => (
  <motion.div
    initial={{ opacity:0, y:18, scale:.97 }}
    whileInView={{ opacity:1, y:0, scale:1 }}
    viewport={{ once:true, margin:"-50px" }}
    transition={{ duration:0.7, delay, ease:easeOut }}
    style={{ flex:"1 1 120px" }}
  >
    <Box
      component={motion.div}
      whileHover={{ y:-5, transition:{ duration:.22, ease:"easeOut" } }}
      sx={{
        position:"relative",
        p:{ xs:"18px 16px", md:"22px 20px" },
        borderRadius:"16px",
        background:"rgba(8,16,32,0.9)",
        border:"1px solid rgba(30,144,255,.13)",
        backdropFilter:"blur(20px)",
        WebkitBackdropFilter:"blur(20px)",
        textAlign:"center",
        overflow:"hidden",
        transition:"border-color .28s ease, box-shadow .28s ease",
        "&:hover": {
          borderColor:"rgba(30,144,255,.32)",
          boxShadow:"0 16px 48px rgba(30,144,255,.1)",
        },
        "&::before":{
          content:'""', position:"absolute",
          top:0, left:"15%", right:"15%", height:"1px",
          background:"linear-gradient(90deg,transparent,rgba(30,144,255,.5),transparent)",
        },
      }}
    >
      {/* Corner micro-glow */}
      <Box sx={{
        position:"absolute", top:-30, right:-20,
        width:80, height:80, borderRadius:"50%",
        background:"radial-gradient(circle,rgba(30,144,255,.14),transparent 70%)",
        filter:"blur(12px)", animation:"ts-breathe 5s ease-in-out infinite",
        pointerEvents:"none",
      }}/>

      <Typography sx={{
        fontFamily:"'Syne',sans-serif", fontWeight:800,
        fontSize:{ xs:"1.8rem", sm:"2rem", md:"2.2rem" },
        letterSpacing:"-0.04em", lineHeight:1,
        background:"linear-gradient(135deg,#fff 0%,rgba(255,255,255,.7) 100%)",
        WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent",
        backgroundClip:"text",
        animation:"ts-countUp .6s ease-out both",
        animationDelay:`${delay + .2}s`,
      }}>
        {value}
      </Typography>
      <Typography sx={{
        fontFamily:"'DM Sans',sans-serif", fontWeight:400,
        fontSize:{ xs:".62rem", md:".68rem" },
        color:"rgba(255,255,255,.36)",
        letterSpacing:".1em", textTransform:"uppercase",
        mt:.8,
      }}>
        {label}
      </Typography>
    </Box>
  </motion.div>
);

/* ══════════════════════════════════════════════════════════════════
   TRAIT TAG
══════════════════════════════════════════════════════════════════ */
const TraitTag = ({ text, delay }) => (
  <motion.div
    initial={{ opacity:0, scale:.92, y:8 }}
    whileInView={{ opacity:1, scale:1, y:0 }}
    viewport={{ once:true }}
    transition={{ duration:.5, delay, ease:easeOut }}
  >
    <Box
      component={motion.div}
      whileHover={{
        y:-3,
        borderColor:"rgba(30,144,255,.45)",
        background:"rgba(30,144,255,.08)",
        transition:{ duration:.18 },
      }}
      sx={{
        display:"inline-flex", alignItems:"center", gap:1,
        px:{ xs:"11px", md:"14px" }, py:{ xs:"6px", md:"8px" },
        borderRadius:"999px",
        background:"rgba(30,144,255,.045)",
        border:"1px solid rgba(30,144,255,.16)",
        backdropFilter:"blur(10px)",
        cursor:"default",
        transition:"all .22s ease",
      }}
    >
      {/* mini dot */}
      <Box sx={{
        width:5, height:5, borderRadius:"50%",
        background:"#1E90FF",
        boxShadow:"0 0 7px rgba(30,144,255,.8)",
        flexShrink:0,
        animation:"ts-dotPulse 2.5s ease-in-out infinite",
        animationDelay:`${delay}s`,
      }}/>
      <Typography sx={{
        fontFamily:"'DM Sans',sans-serif", fontWeight:500,
        fontSize:{ xs:".75rem", md:".8rem" },
        color:"rgba(255,255,255,.58)",
        letterSpacing:".02em",
        userSelect:"none",
      }}>
        {text}
      </Typography>
    </Box>
  </motion.div>
);

/* ══════════════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════════════ */
const TeamSection = ({ SectionWrap, SectionHeading, fadeUp }) => {
  const sectionRef = useRef(null);
  const [pos, setPos] = useState({ x:"50%", y:"50%" });

  /* Mouse follower glow */
  const handleMouse = (e) => {
    const rect = sectionRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPos({
      x: ((e.clientX - rect.left)/rect.width*100).toFixed(1)+"%",
      y: ((e.clientY - rect.top)/rect.height*100).toFixed(1)+"%",
    });
  };

  return (
    <>
      <GlobalStyles />

      <Box
        ref={sectionRef}
        onMouseMove={handleMouse}
        sx={{
          position:"relative",
          py:{ xs:"72px", sm:"88px", md:"104px" },
          backgroundColor:"#000",
          overflow:"hidden",
        }}
      >
        {/* ── Background layers ── */}
        <Particles />

        {/* Grid fabric */}
        <Box aria-hidden sx={{
          position:"absolute", inset:0, pointerEvents:"none", zIndex:1,
          backgroundImage:
            "linear-gradient(rgba(30,144,255,.022) 1px,transparent 1px)," +
            "linear-gradient(90deg,rgba(30,144,255,.022) 1px,transparent 1px)",
          backgroundSize:"60px 60px",
          maskImage:"radial-gradient(ellipse 70% 65% at 50% 50%,black,transparent)",
        }}/>

        {/* Ambient top-right orb */}
        <Box aria-hidden sx={{
          position:"absolute", top:"-20%", right:"-8%",
          width:{ xs:200, md:440 }, height:{ xs:200, md:440 },
          borderRadius:"50%",
          background:"radial-gradient(circle,rgba(30,144,255,.09),transparent 68%)",
          filter:"blur(60px)",
          animation:"ts-orb 14s ease-in-out infinite",
          pointerEvents:"none", zIndex:0,
        }}/>

        {/* Ambient bottom-left orb */}
        <Box aria-hidden sx={{
          position:"absolute", bottom:"-18%", left:"-8%",
          width:{ xs:180, md:380 }, height:{ xs:180, md:380 },
          borderRadius:"50%",
          background:"radial-gradient(circle,rgba(30,144,255,.06),transparent 68%)",
          filter:"blur(70px)",
          animation:"ts-orb 18s ease-in-out infinite reverse",
          animationDelay:"-6s",
          pointerEvents:"none", zIndex:0,
        }}/>

        {/* Mouse glow */}
        <Box aria-hidden sx={{
          position:"absolute", pointerEvents:"none", zIndex:2, inset:0, overflow:"hidden",
        }}>
          <Box sx={{
            position:"absolute",
            width:{ xs:280, md:520 }, height:{ xs:280, md:520 },
            borderRadius:"50%",
            background:"radial-gradient(circle,rgba(30,144,255,.058),transparent 63%)",
            filter:"blur(50px)",
            left:pos.x, top:pos.y,
            transform:"translate(-50%,-50%)",
            transition:"left .3s ease,top .3s ease",
            pointerEvents:"none",
          }}/>
        </Box>

        {/* Sweep line */}
        <Box aria-hidden sx={{
          position:"absolute", top:"38%", left:0, right:0,
          height:"1px",
          background:"linear-gradient(90deg,transparent,rgba(30,144,255,.22) 40%,rgba(30,144,255,.38) 55%,transparent)",
          animation:"ts-scanX 11s ease-in-out infinite",
          pointerEvents:"none", zIndex:1,
        }}/>

        {/* ══ CONTENT ══ */}
        <Container
          maxWidth="lg"
          sx={{
            position:"relative", zIndex:5,
            px:{ xs:"18px", sm:"28px", md:"44px", lg:"60px" },
          }}
        >
          {/* ── Section label ── */}
          <motion.div {...inView(0.04)}>
            <Box sx={{ display:"flex", alignItems:"center", justifyContent:"center", mb:{ xs:3, md:4 } }}>
              <Box sx={{
                display:"inline-flex", alignItems:"center", gap:1.4,
                px:"14px", py:"7px",
                borderRadius:"999px",
                background:"rgba(30,144,255,.055)",
                border:"1px solid rgba(30,144,255,.18)",
                position:"relative",
              }}>
                {/* ping */}
                <Box
                  component={motion.div}
                  animate={{ scale:[1,2.8], opacity:[.6,0] }}
                  transition={{ duration:2.2, repeat:Infinity, ease:"easeOut" }}
                  sx={{ position:"absolute", left:13, width:6, height:6, borderRadius:"50%", background:"#1E90FF" }}
                />
                <Box sx={{
                  width:6, height:6, borderRadius:"50%",
                  background:"#1E90FF", boxShadow:"0 0 12px rgba(30,144,255,.9)",
                  flexShrink:0, zIndex:1,
                }}/>
                <Typography sx={{
                  fontFamily:"'DM Sans',sans-serif", fontWeight:600,
                  fontSize:{ xs:".67rem", sm:".72rem" },
                  color:"rgba(30,144,255,.82)",
                  letterSpacing:".14em", textTransform:"uppercase",
                }}>
                  Our Team
                </Typography>
              </Box>
            </Box>
          </motion.div>

          {/* ── Main heading ── */}
          <motion.div {...inView(0.1)}>
            <Box sx={{ textAlign:"center", mb:{ xs:1.5, md:2 } }}>
              <Typography
                component="h2"
                sx={{
                  fontFamily:"'Syne',sans-serif", fontWeight:800,
                  fontSize:{ xs:"2.2rem", sm:"2.8rem", md:"3.4rem", lg:"4rem" },
                  lineHeight:{ xs:1.12, md:1.06 },
                  letterSpacing:{ xs:"-1px", md:"-2px", lg:"-2.5px" },
                  color:"rgba(255,255,255,.97)",
                  m:0,
                }}
              >
                Passionate{" "}
                <Box component="span" sx={{
                  background:"linear-gradient(130deg,#1E90FF 0%,#60B4FF 42%,#93C5FD 70%,#C7E4FF 100%)",
                  WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent",
                  backgroundClip:"text",
                  backgroundSize:"180% auto",
                  animation:"ts-shimmer 4.5s linear infinite",
                }}>
                  &amp; Dedicated
                </Box>
              </Typography>
            </Box>
          </motion.div>

          {/* ── Subtitle ── */}
          <motion.div {...inView(0.16)}>
            <Typography sx={{
              fontFamily:"'DM Sans',sans-serif", fontWeight:300,
              fontSize:{ xs:".92rem", sm:".98rem", md:"1.05rem" },
              color:"rgba(255,255,255,.44)",
              lineHeight:1.9,
              textAlign:"center",
              maxWidth:{ xs:"100%", sm:560, md:660 },
              mx:"auto",
              mb:{ xs:5, md:6 },
            }}>
              Our team at Anthrasync consists of highly passionate, dedicated and motivated individuals who are
              continuously working towards one common goal — continuously updating their skills as part of a
              relentless process of learning and growth.
            </Typography>
          </motion.div>

          {/* ── Stats row ── */}
          <motion.div {...inView(0.2)}>
            <Box sx={{
              display:"flex",
              flexWrap:"wrap",
              gap:{ xs:2, md:2.5 },
              justifyContent:"center",
              mb:{ xs:5, md:6 },
              maxWidth:{ xs:"100%", md:720 },
              mx:"auto",
            }}>
              {STATS.map((s, i) => (
                <StatCard key={s.label} value={s.value} label={s.label} delay={0.24 + i*0.07} />
              ))}
            </Box>
          </motion.div>

          {/* ── Main glass card ── */}
          <motion.div {...inView(0.28)}>
            <Box
              component={motion.div}
              whileHover={{ y:-6, transition:{ duration:.28, ease:"easeOut" } }}
              sx={{
                position:"relative",
                maxWidth:{ xs:"100%", sm:680, md:760 },
                mx:"auto",
                borderRadius:"22px",
                overflow:"hidden",
                background:"linear-gradient(155deg,rgba(8,16,34,.94) 0%,rgba(4,8,18,.97) 100%)",
                border:"1px solid rgba(30,144,255,.15)",
                animation:"ts-borderGlow 5s ease-in-out infinite",
                backdropFilter:"blur(28px)",
                WebkitBackdropFilter:"blur(28px)",
                p:{ xs:"28px 22px 32px", sm:"36px 32px 40px", md:"44px 44px 48px" },
                "&::before":{
                  content:'""', position:"absolute",
                  top:0, left:"12%", right:"12%", height:"1px",
                  background:"linear-gradient(90deg,transparent,rgba(30,144,255,.55),transparent)",
                },
                "&::after":{
                  content:'""', position:"absolute",
                  bottom:0, left:"25%", right:"25%", height:"1px",
                  background:"linear-gradient(90deg,transparent,rgba(30,144,255,.18),transparent)",
                },
              }}
            >
              {/* Inner corner glows */}
              <Box sx={{
                position:"absolute", top:-60, right:-40,
                width:200, height:200, borderRadius:"50%",
                background:"radial-gradient(circle,rgba(30,144,255,.16),transparent 68%)",
                filter:"blur(24px)", animation:"ts-breathe 5.5s ease-in-out infinite",
                pointerEvents:"none",
              }}/>
              <Box sx={{
                position:"absolute", bottom:-40, left:-30,
                width:160, height:160, borderRadius:"50%",
                background:"radial-gradient(circle,rgba(30,144,255,.1),transparent 68%)",
                filter:"blur(20px)", animation:"ts-breathe 7.5s ease-in-out infinite reverse",
                pointerEvents:"none",
              }}/>

              {/* Card inner scan */}
              <Box sx={{
                position:"absolute", top:0, left:0, right:0,
                height:"40%",
                background:"linear-gradient(to bottom,transparent,rgba(30,144,255,.02),transparent)",
                animation:"ts-scanX 8s linear infinite",
                pointerEvents:"none", zIndex:2,
              }}/>

              {/* ── Card header row ── */}
              <Box sx={{
                display:"flex", alignItems:"center",
                justifyContent:{ xs:"center", sm:"space-between" },
                flexWrap:"wrap", gap:2,
                mb:{ xs:3, md:3.5 },
                position:"relative", zIndex:3,
              }}>
                {/* Left: icon + text */}
                <Box sx={{ display:"flex", alignItems:"center", gap:2 }}>
                  {/* Animated icon box */}
                  <Box
                    component={motion.div}
                    animate={{ y:[0,-6,0] }}
                    transition={{ duration:3.5, repeat:Infinity, ease:"easeInOut" }}
                    sx={{
                      width:{ xs:44, md:50 }, height:{ xs:44, md:50 },
                      borderRadius:"14px",
                      background:"rgba(30,144,255,.09)",
                      border:"1px solid rgba(30,144,255,.24)",
                      display:"flex", alignItems:"center", justifyContent:"center",
                      flexShrink:0,
                      boxShadow:"0 0 22px rgba(30,144,255,.14)",
                      position:"relative",
                    }}
                  >
                    {/* Ring pulse */}
                    <Box
                      component={motion.div}
                      animate={{ scale:[1,1.9], opacity:[.5,0] }}
                      transition={{ duration:2.2, repeat:Infinity, ease:"easeOut" }}
                      sx={{
                        position:"absolute", inset:0,
                        borderRadius:"14px",
                        border:"1px solid rgba(30,144,255,.5)",
                      }}
                    />
                    {/* Team icon as SVG */}
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                      <circle cx="9" cy="7" r="3.2" stroke="#1E90FF" strokeWidth="1.4"/>
                      <path d="M3 19c0-3.3 2.7-6 6-6h0c3.3 0 6 2.7 6 6" stroke="#1E90FF" strokeWidth="1.4" strokeLinecap="round"/>
                      <circle cx="17" cy="8" r="2.4" stroke="rgba(30,144,255,.55)" strokeWidth="1.2"/>
                      <path d="M15.5 19c.3-2.5 2.2-4.5 4.5-4.5" stroke="rgba(30,144,255,.55)" strokeWidth="1.2" strokeLinecap="round"/>
                    </svg>
                  </Box>

                  <Box>
                    <Typography sx={{
                      fontFamily:"'Syne',sans-serif", fontWeight:700,
                      fontSize:{ xs:"1rem", md:"1.06rem" },
                      color:"rgba(255,255,255,.9)", letterSpacing:"-.01em", lineHeight:1.2,
                    }}>
                      People Behind the Mission
                    </Typography>
                    <Typography sx={{
                      fontFamily:"'DM Sans',sans-serif", fontWeight:400,
                      fontSize:".65rem", color:"rgba(30,144,255,.65)", mt:.35, lineHeight:1,
                    }}>
                      Anthrasync · Core Team
                    </Typography>
                  </Box>
                </Box>

                {/* Right: LIVE badge */}
                <Box sx={{
                  display:"flex", alignItems:"center", gap:.8,
                  px:"10px", py:"5px",
                  borderRadius:"999px",
                  background:"rgba(34,211,161,.07)",
                  border:"1px solid rgba(34,211,161,.18)",
                }}>
                  <Box sx={{
                    width:5, height:5, borderRadius:"50%",
                    background:"#22d3a1",
                    boxShadow:"0 0 8px rgba(34,211,161,.8)",
                    animation:"ts-blink 2.4s ease-in-out infinite",
                  }}/>
                  <Typography sx={{
                    fontFamily:"'DM Sans',sans-serif", fontSize:".6rem",
                    color:"rgba(34,211,161,.85)", fontWeight:600, letterSpacing:".08em",
                  }}>
                    GROWING
                  </Typography>
                </Box>
              </Box>

              {/* Divider */}
              <Box sx={{
                height:"1px", mb:{ xs:3, md:3.5 }, position:"relative", zIndex:3,
                background:"linear-gradient(90deg,rgba(30,144,255,.22),rgba(30,144,255,.08),transparent)",
                overflow:"hidden",
              }}>
                <Box sx={{
                  position:"absolute", inset:0,
                  background:"linear-gradient(90deg,rgba(30,144,255,.5),transparent)",
                  animation:"ts-lineGrow 1.2s ease-out .5s both",
                }}/>
              </Box>

              {/* ── Body text ── */}
              <Typography sx={{
                fontFamily:"'DM Sans',sans-serif", fontWeight:300,
                fontSize:{ xs:".9rem", sm:".94rem", md:"1rem" },
                color:"rgba(255,255,255,.52)",
                lineHeight:1.95,
                letterSpacing:".015em",
                mb:{ xs:3.5, md:4 },
                position:"relative", zIndex:3,
              }}>
                Our team at Anthrasync consists of highly passionate, dedicated and motivated individuals who are
                continuously working towards one common goal. We are always willing to take risks, face challenges
                and overcome them. We make sure that we are regularly updating our skills and are part of a
                continuous process of learning and growth.
              </Typography>

              {/* ── Trait tags ── */}
              <Box sx={{
                display:"flex", flexWrap:"wrap",
                gap:{ xs:1.2, md:1.5 },
                position:"relative", zIndex:3,
              }}>
                {TRAITS.map((t, i) => (
                  <TraitTag key={t} text={t} delay={0.38 + i*0.07} />
                ))}
              </Box>

              {/* ── Bottom status bar ── */}
              <Box sx={{
                mt:{ xs:3.5, md:4 },
                pt:{ xs:2.5, md:3 },
                borderTop:"1px solid rgba(30,144,255,.08)",
                display:"flex", alignItems:"center",
                justifyContent:{ xs:"center", sm:"space-between" },
                flexWrap:"wrap", gap:1.5,
                position:"relative", zIndex:3,
              }}>
                {/* Left: pulse + text */}
                <Box sx={{ display:"flex", alignItems:"center", gap:1.2 }}>
                  <Box sx={{
                    width:4, height:4, borderRadius:"50%",
                    background:"#1E90FF", boxShadow:"0 0 7px rgba(30,144,255,.8)",
                    animation:"ts-dotPulse 2s ease-in-out infinite",
                    flexShrink:0,
                  }}/>
                  <Typography sx={{
                    fontFamily:"'DM Sans',sans-serif", fontSize:".66rem",
                    color:"rgba(255,255,255,.3)", fontWeight:400,
                  }}>
                    Distributed across{" "}
                    <Box component="span" sx={{ color:"rgba(30,144,255,.7)", fontWeight:600 }}>8 cities</Box>
                    {" "}· Remote-first culture
                  </Typography>
                </Box>

                {/* Right: avatar stack placeholder */}
                <Box sx={{ display:"flex", alignItems:"center", gap:-.8 }}>
                  {[.9, .75, .6, .48].map((op, i) => (
                    <Box key={i} sx={{
                      width:{ xs:26, md:28 }, height:{ xs:26, md:28 },
                      borderRadius:"50%",
                      background:`rgba(30,144,255,${op*.22})`,
                      border:"1.5px solid rgba(30,144,255,.24)",
                      ml: i>0 ? "-8px" : 0,
                      boxShadow:"0 0 0 1.5px #000",
                      display:"flex", alignItems:"center", justifyContent:"center",
                    }}>
                      <Box sx={{
                        width:8, height:8, borderRadius:"50%",
                        background:`rgba(30,144,255,${op*.6})`,
                      }}/>
                    </Box>
                  ))}
                  <Typography sx={{
                    fontFamily:"'DM Sans',sans-serif", fontSize:".62rem",
                    color:"rgba(255,255,255,.3)", ml:1.2,
                  }}>
                    +36 more
                  </Typography>
                </Box>
              </Box>
            </Box>
          </motion.div>

        </Container>
      </Box>
    </>
  );
};

export default TeamSection;